import type { ISpeakerHypothesisSnapshot } from "@lexicanon/shared"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Button } from "@mantine/core"
import { IconUsers } from "@tabler/icons-react"
import { useEffect, useState } from "react"
import { speakerDisplayLabel, speakerVMFromName } from "../../lib/speaker-vm"
import classes from "./SpeakerHypothesisStrip.module.css"

/** High enough to say "likely". A weaker guess still shows, without a strength word. */
const LIKELY_SCORE = 0.8

function liveSpeakerVM(h: ISpeakerHypothesisSnapshot): ReturnType<typeof speakerVMFromName> {
  return {
    ...speakerVMFromName(h.label),
    label: h.label,
    displayIndex: h.displayIndex ?? -1,
    status: "unidentified" as const,
    needsAttention: true,
  }
}

/**
 * Top non-rejected candidate as a suggested VM — used by the live transcript
 * panel's compact chips. Does not apply a client-side score cutoff: the backend
 * already filtered to the org threshold.
 */
export function suggestedSpeakerVM(h: ISpeakerHypothesisSnapshot): ReturnType<typeof speakerVMFromName> | null {
  const visible = h.candidates.filter((c) => !c.rejected)
  if (visible.length === 0) return null
  const top = visible.reduce((best, c) => (c.score > best.score ? c : best))
  return {
    ...speakerVMFromName(h.label),
    label: h.label,
    displayIndex: h.displayIndex ?? -1,
    status: "suggested" as const,
    needsAttention: true,
    suggestion: { name: top.name, score: top.score, sources: top.sources },
  }
}

interface IProps {
  /** Overline above the rows. Defaults to "Identifying speakers". */
  heading?: React.ReactNode
  /** Live per-label hypotheses (as an array). Only tentative ones with an
   * above-threshold candidate render. */
  hypotheses: ISpeakerHypothesisSnapshot[]
  /**
   * When false, render display-only chips: the `{name}?` recognition badges show
   * (so an observer sees the pipeline identifying speakers) but the confirm /
   * reject controls are omitted. For a live-meeting observer without speaker-edit
   * rights. `pending` / `onAction` are unused in this mode. Default `true`.
   */
  interactive?: boolean
  /** Pending action keys, namespaced `${label}|${name}|${action}`. Interactive mode only. */
  pending?: Set<string>
  onAction?: (label: string, name: string, action: "confirm" | "reject") => void
}

/** Stable empty set so the display-only default doesn't churn identity. */
const EMPTY_PENDING: Set<string> = new Set()

/**
 * SpeakerHypothesisStrip — surfaces live speaker-recognition guesses in the new
 * recording focus view. Each row reads "Speaker 2 · sounds like Gerard", with
 * "likely" when the guess is strong, plus Confirm / Not them. The box height
 * animates in when the first tentative speaker lands, and each row pops in via
 * the shared motion keyframe. Pure + dumb: the smart wrapper owns the
 * confirm/reject API call + pending state.
 */
export function SpeakerHypothesisStrip({
  heading,
  hypotheses,
  interactive = true,
  pending = EMPTY_PENDING,
  onAction,
}: IProps): React.JSX.Element | null {
  const { i18n } = useLingui()
  const rows = hypotheses.filter((h) => h.status !== "confirmed" && h.candidates.some((c) => !c.rejected))
  const hasContent = rows.length > 0

  // Open (height-expand) the frame after mount so the box slides in rather than
  // appearing at full height. Reduced motion collapses the transition to instant
  // via the zeroed duration tokens.
  const [open, setOpen] = useState(false)
  useEffect(() => {
    if (!hasContent) {
      setOpen(false)
      return
    }
    const id = requestAnimationFrame(() => setOpen(true))
    return () => cancelAnimationFrame(id)
  }, [hasContent])

  if (!hasContent) return null

  return (
    <div className={classes.reveal} data-open={open ? "true" : "false"} data-testid="speaker-hypothesis-strip">
      <div className={classes.revealInner}>
        <div className={classes.strip}>
          <span className={classes.overline}>
            <IconUsers size={12} />
            {heading ?? <Trans>Identifying speakers</Trans>}
          </span>
          <div className={classes.rows}>
            {rows.map((h) => {
              const vm = liveSpeakerVM(h)
              const visible = h.candidates.filter((c) => !c.rejected)
              let top = visible[0]
              if (!top) return null
              for (const candidate of visible) {
                if (candidate.score > top.score) top = candidate
              }
              const label = speakerDisplayLabel(vm, i18n)
              const name = top.name
              const likely = top.score >= LIKELY_SCORE
              const confirmKey = `${h.label}|${name}|confirm`
              const rejectKey = `${h.label}|${name}|reject`
              return (
                <div key={h.label} className={classes.row} data-testid="speaker-hypothesis-row">
                  <span className={classes.label}>
                    {likely ? (
                      <Trans>
                        {label} · likely sounds like <strong>{name}</strong>
                      </Trans>
                    ) : (
                      <Trans>
                        {label} · sounds like <strong>{name}</strong>
                      </Trans>
                    )}
                  </span>
                  {interactive ? (
                    <>
                      <Button
                        size="compact-xs"
                        variant="light"
                        loading={pending.has(confirmKey)}
                        disabled={pending.has(rejectKey)}
                        onClick={() => onAction?.(h.label, name, "confirm")}
                      >
                        <Trans>Confirm</Trans>
                      </Button>
                      <Button
                        size="compact-xs"
                        variant="subtle"
                        loading={pending.has(rejectKey)}
                        disabled={pending.has(confirmKey)}
                        onClick={() => onAction?.(h.label, name, "reject")}
                      >
                        <Trans>Not them</Trans>
                      </Button>
                    </>
                  ) : null}
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
